import { useCallback, useState } from "react";
import {
  createManufacturingDealerLocation,
  updateManufacturingDealerLocation,
} from "@/lib/manufacturing/dealersClient";
import type { MfgDealerLocation } from "@/lib/manufacturing/dealerTypes";
import {
  EMPTY_LOCATION_DRAFT,
  buildAddressPayload,
  isRealPlantSelection,
  locationToDraft,
  type LocationDraft,
  type PlantSelection,
} from "./plantDealersDrafts";

type BusySetter = (busy: boolean) => void;
type ErrorSetter = (error: string | null) => void;

export function usePlantDealersLocationActions(
  selection: PlantSelection,
  reload: () => Promise<void>,
  setActionBusy: BusySetter,
  setLocalError: ErrorSetter
) {
  const [creatingLocation, setCreatingLocation] = useState(false);
  const [locationCreateDraft, setLocationCreateDraft] = useState<LocationDraft>({
    ...EMPTY_LOCATION_DRAFT,
  });
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [locationEditDraft, setLocationEditDraft] = useState<LocationDraft>({
    ...EMPTY_LOCATION_DRAFT,
  });

  const startCreateLocation = useCallback(() => {
    const initialPlantId = isRealPlantSelection(selection) ? (selection as string) : "";
    setCreatingLocation(true);
    setLocationCreateDraft({ ...EMPTY_LOCATION_DRAFT, primary_plant_id: initialPlantId });
    setEditingLocationId(null);
    setLocalError(null);
  }, [selection, setLocalError]);

  const cancelCreateLocation = useCallback(() => {
    setCreatingLocation(false);
    setLocalError(null);
  }, [setLocalError]);

  const submitCreateLocation = useCallback(async () => {
    const d = locationCreateDraft;
    if (!d.name.trim()) {
      setLocalError("Dealer location name is required.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await createManufacturingDealerLocation({
      name: d.name.trim(),
      address: buildAddressPayload(d),
      primary_plant_id: d.primary_plant_id.trim() || null,
      linked_dealer_org_id: d.linked_dealer_org_id.trim() || null,
      contact_name: d.contact_name.trim(),
      contact_phone: d.contact_phone.trim(),
      contact_email: d.contact_email.trim(),
      notes: d.notes,
      is_active: d.is_active,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setCreatingLocation(false);
    setLocationCreateDraft({ ...EMPTY_LOCATION_DRAFT });
    await reload();
  }, [locationCreateDraft, reload, setActionBusy, setLocalError]);

  const startEditLocation = useCallback(
    (loc: MfgDealerLocation) => {
      setEditingLocationId(loc.id);
      setLocationEditDraft(locationToDraft(loc));
      setCreatingLocation(false);
      setLocalError(null);
    },
    [setLocalError]
  );

  const cancelEditLocation = useCallback(() => {
    setEditingLocationId(null);
    setLocalError(null);
  }, [setLocalError]);

  const submitEditLocation = useCallback(async () => {
    if (!editingLocationId) return;
    const d = locationEditDraft;
    if (!d.name.trim()) {
      setLocalError("Dealer location name is required.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await updateManufacturingDealerLocation({
      location_id: editingLocationId,
      name: d.name.trim(),
      address: buildAddressPayload(d),
      primary_plant_id: d.primary_plant_id.trim() || null,
      linked_dealer_org_id: d.linked_dealer_org_id.trim() || null,
      contact_name: d.contact_name.trim(),
      contact_phone: d.contact_phone.trim(),
      contact_email: d.contact_email.trim(),
      notes: d.notes,
      is_active: d.is_active,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setEditingLocationId(null);
    await reload();
  }, [editingLocationId, locationEditDraft, reload, setActionBusy, setLocalError]);

  const toggleLocationActive = useCallback(
    async (loc: MfgDealerLocation, next: boolean) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateManufacturingDealerLocation({
        location_id: loc.id,
        is_active: next,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload, setActionBusy, setLocalError]
  );

  const detachLocation = useCallback(
    async (loc: MfgDealerLocation) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateManufacturingDealerLocation({
        location_id: loc.id,
        primary_plant_id: null,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload, setActionBusy, setLocalError]
  );

  return {
    creatingLocation,
    locationCreateDraft,
    setLocationCreateDraft,
    startCreateLocation,
    cancelCreateLocation,
    submitCreateLocation,
    editingLocationId,
    locationEditDraft,
    setLocationEditDraft,
    startEditLocation,
    cancelEditLocation,
    submitEditLocation,
    toggleLocationActive,
    detachLocation,
  };
}
